import { DateTime } from "luxon"

import { calculateTotal } from "./table"
import { formatCurrency } from "./string"
import { FormattedRateflowCard, RateflowCard } from "../../types"

export enum RateTableUsageType {
  IMPRESSIONS = "Impressions",
  UPDATE_RATES_CLICK = "Update Rates Click",
  GET_STARTED_CLICK = "Get Started Click",
  PERSONALIZED_RATES_CLICK = "Personalized Rates Click",
  GET_RATE_ALERTS_CLICK = "Get Rate Alerts Click"
}

// Lightest (base/largest metric) to darkest (smallest metric), matching the
// project's primary-200 -> primary-600 tokens (tailwind.config.js). The
// biggest metric (Impressions) covers most of the stacked area, so it gets
// the lightest tone to keep the whole chart feeling airy - the darkest tone
// only shows up as a thin accent sliver for the smallest metric on top.
export const RateTableUsageTypeColor = {
  IMPRESSIONS: "#786FF6", // primary-200
  UPDATE_RATES_CLICK: "#7042BE", // primary-300
  GET_STARTED_CLICK: "#5016A3", // primary-400
  PERSONALIZED_RATES_CLICK: "#3D1080", // primary-500
  GET_RATE_ALERTS_CLICK: "#2E0C68" // primary-600
} as const

export interface RateTableInfoData {
  label: string
  value: string
  containerClassName?: string
  iconColor?: string
  containsTooltip?: boolean
  tooltipText?: string
}

export const getShortFeeTitle = (fee: { title?: string }) => {
  return (fee.title ?? "").split(" ").pop()
}

export const getDefaultSeriesData = (): Record<
  RateTableUsageType,
  number[]
> => {
  const initialData: Record<RateTableUsageType, number[]> = {
    [RateTableUsageType.IMPRESSIONS]: [],
    [RateTableUsageType.UPDATE_RATES_CLICK]: [],
    [RateTableUsageType.GET_STARTED_CLICK]: [],
    [RateTableUsageType.PERSONALIZED_RATES_CLICK]: [],
    [RateTableUsageType.GET_RATE_ALERTS_CLICK]: []
  }

  for (let i = 0; i < 6; i++) {
    Object.values(RateTableUsageType).forEach(type => initialData[type].push(0))
  }

  return initialData
}

export const getDefaultNoDataDates = (labelDateFormat: string): string[] => {
  return Array.from({ length: 6 }, (_, i) =>
    DateTime.now().minus({ days: i }).toFormat(labelDateFormat)
  ).sort((a, b) => (a > b ? 1 : -1))
}

// Solid (fully opaque) light tint of a palette color, obtained by mixing it
// with white. Unlike an alpha-blended fill, this never compounds/darkens
// when areas are stacked, since there's no transparency for layers to add up
// through - each band renders as one flat, readable color.
const mixWithWhite = (hex: string, whiteRatio: number): string => {
  const mixChannel = (channel: number): number =>
    Math.round(channel * (1 - whiteRatio) + 255 * whiteRatio)
  const r = mixChannel(parseInt(hex.slice(1, 3), 16))
  const g = mixChannel(parseInt(hex.slice(3, 5), 16))
  const b = mixChannel(parseInt(hex.slice(5, 7), 16))
  return `rgb(${r}, ${g}, ${b})`
}

export const createRateTableUsageSeries = (
  name: RateTableUsageType,
  color: string,
  emphasizeLine: boolean = false
): Highcharts.SeriesAreasplineOptions => ({
  type: "areaspline",
  name,
  data: [],
  color,
  lineWidth: emphasizeLine ? 3 : 1.75,
  fillOpacity: 1,
  fillColor: {
    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
    stops: [
      [0, mixWithWhite(color, 0.68)],
      [1, mixWithWhite(color, 0.78)]
    ]
  },
  marker: {
    enabled: false,
    symbol: "circle"
  }
})

export const createRateTableInfoData = (
  label: string,
  value: string,
  iconColor?: string,
  containsTooltip?: boolean,
  tooltipText?: string,
  containerClassName?: string
): RateTableInfoData => ({
  label,
  value,
  iconColor,
  containsTooltip,
  tooltipText,
  containerClassName
})

export const getRateTableInfoDataValue = (
  containsData: boolean | undefined,
  seriesData: Record<RateTableUsageType, number[]> | undefined,
  type: RateTableUsageType
): string =>
  containsData && seriesData
    ? calculateTotal(seriesData[type])?.toLocaleString("en-US")
    : "-"

export const getRateFlowFeeLabel = (
  rateFlow: RateflowCard | FormattedRateflowCard | undefined
): string => {
  if (!rateFlow) return "Fee"

  return rateFlow.rebate != null && rateFlow.rebate !== 0 ? "Credit" : "Fee"
}

export const getRateFlowFeeValue = (
  rateFlow: RateflowCard | FormattedRateflowCard | undefined
): string => {
  if (!rateFlow) return "-"

  const { rebate, fee } = rateFlow

  return rebate != null && rebate !== 0
    ? formatCurrency(rebate)
    : formatCurrency(fee.amount)
}
